/**
 * The Company page's first-visit explainer (ADR-022): what a Company is, what
 * linking a workspace carries (billing, administration, arche approvals, and —
 * where Fias has enabled it — the Company's brand, ADR-023/024) and —
 * just as important — what it leaves alone. Shown only while the user holds
 * no Company role; the Create card sits directly beneath it.
 *
 * The diagram is inline SVG drawn from the theme tokens (`fill-primary`,
 * `stroke-border`, …) so it follows light/dark like the rest of the page and
 * scales with its container.
 */

import { Building2, CreditCard, Palette, ShieldCheck, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

const WORKSPACES = [
  { name: 'HR', x: 80, placeholder: false },
  { name: 'Finance', x: 300, placeholder: false },
  { name: 'Any workspace', x: 520, placeholder: true },
] as const;

/** What the Company unifies — drawn INSIDE the Company, not on the links. */
const UNIFIES = ['Billing', 'Administration', 'Arche approvals', 'Branding'] as const;

function LinkDiagram() {
  const companyX = 360;
  const companyTop = 20;
  const companyHeight = 112;
  const companyBottom = companyTop + companyHeight;
  const workspaceTop = 212;
  // Pills inside the Company: sized to their label, laid out as one centred row.
  const pillWidths = [76, 114, 118, 84];
  const pillGap = 8;
  const rowWidth = pillWidths.reduce((a, b) => a + b, 0) + pillGap * (pillWidths.length - 1);
  let pillX = companyX - rowWidth / 2;
  const pills = UNIFIES.map((label, i) => {
    const x = pillX;
    pillX += pillWidths[i] + pillGap;
    return { label, x, width: pillWidths[i] };
  });
  return (
    <svg
      viewBox="0 0 720 300"
      role="img"
      aria-labelledby="company-diagram-title company-diagram-desc"
      className="w-full max-w-3xl mx-auto h-auto font-sans"
    >
      <title id="company-diagram-title">How a Company relates to workspaces</title>
      <desc id="company-diagram-desc">
        One Company, which unifies billing, administration, arche approvals and branding, above the
        workspaces it links: HR, Finance and any other workspace. Each workspace keeps its own
        roster, roles and data.
      </desc>

      {/* Connectors: Company → each workspace */}
      {WORKSPACES.map((w) => {
        const cx = w.x + 60;
        const midY = (companyBottom + workspaceTop) / 2;
        return (
          <path
            key={w.name}
            d={`M ${companyX} ${companyBottom} C ${companyX} ${midY}, ${cx} ${midY}, ${cx} ${workspaceTop}`}
            className="stroke-primary/50"
            strokeWidth={2}
            strokeDasharray={w.placeholder ? '5 5' : undefined}
            fill="none"
          />
        );
      })}

      {/* The Company, with the three things it unifies inside it */}
      <rect
        x={companyX - 225}
        y={companyTop}
        width={450}
        height={companyHeight}
        rx={16}
        className="fill-primary"
      />
      <Building2
        x={companyX - 205}
        y={companyTop + 20}
        width={30}
        height={30}
        className="stroke-primary-foreground"
        strokeWidth={1.75}
      />
      <text
        x={companyX - 165}
        y={companyTop + 33}
        className="fill-primary-foreground"
        fontSize={17}
        fontWeight={600}
      >
        Your Company
      </text>
      <text
        x={companyX - 165}
        y={companyTop + 53}
        className="fill-primary-foreground/80"
        fontSize={12}
      >
        holds no data of its own · unifies:
      </text>
      {pills.map((p) => (
        <g key={p.label}>
          <rect
            x={p.x}
            y={companyTop + 70}
            width={p.width}
            height={26}
            rx={13}
            className="fill-primary-foreground/15 stroke-primary-foreground/40"
            strokeWidth={1}
          />
          <text
            x={p.x + p.width / 2}
            y={companyTop + 87}
            textAnchor="middle"
            className="fill-primary-foreground"
            fontSize={12.5}
            fontWeight={600}
          >
            {p.label}
          </text>
        </g>
      ))}

      {/* The workspaces */}
      {WORKSPACES.map((w) => (
        <g key={w.name}>
          <rect
            x={w.x}
            y={workspaceTop}
            width={120}
            height={72}
            rx={12}
            className="fill-card stroke-border"
            strokeWidth={1.5}
            strokeDasharray={w.placeholder ? '5 5' : undefined}
          />
          <text
            x={w.x + 60}
            y={workspaceTop + 22}
            textAnchor="middle"
            className="fill-muted-foreground"
            fontSize={10.5}
            letterSpacing={0.6}
          >
            WORKSPACE
          </text>
          <text
            x={w.x + 60}
            y={workspaceTop + 44}
            textAnchor="middle"
            className="fill-foreground"
            fontSize={15}
            fontWeight={600}
            fontStyle={w.placeholder ? 'italic' : undefined}
          >
            {w.name}
          </text>
          <text
            x={w.x + 60}
            y={workspaceTop + 62}
            textAnchor="middle"
            className="fill-muted-foreground"
            fontSize={10.5}
          >
            own roster/roles/data
          </text>
        </g>
      ))}
    </svg>
  );
}

const PILLARS = [
  {
    icon: CreditCard,
    title: 'One payer',
    body: 'Every linked workspace bills to the Company’s nominated payer — always one of its owners — switched the moment a workspace links or unlinks.',
  },
  {
    icon: Users,
    title: 'One place to administer',
    body: 'Owners and admins link workspaces, manage the roster and approve arches, and see which Company owners are on each workspace’s roster; owners alone unlink workspaces and hold the payer role. Company roles are management only.',
  },
  {
    icon: ShieldCheck,
    title: 'One place to approve arches',
    body: 'Approve an arche once and it may work with every linked workspace’s data — at each member’s own role, never more. Any workspace can still deny it, and revoking is immediate.',
  },
  {
    icon: Palette,
    title: 'One brand',
    body: 'Your name, logo and colours on the signing pages and emails your workspaces send, and on your own Company portal — set once for every linked workspace. Available once Fias enables branding for your Company.',
  },
] as const;

export function CompanyExplainer({ onCreate }: { onCreate?: () => void }) {
  return (
    <section
      aria-labelledby="company-explainer-heading"
      className="rounded-xl border border-border bg-card px-4 py-8 sm:px-8"
    >
      <div className="mx-auto max-w-2xl text-center">
        <h2
          id="company-explainer-heading"
          className="text-xl font-semibold text-foreground text-balance"
        >
          One Company over all your workspaces
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Workspaces are where your team’s data and roster live. A Company sits above them and
          unifies billing, administration, arche approvals and your brand across the workspaces it
          links — while each workspace stays exactly as it is.
        </p>
      </div>

      {/* Hidden on a phone: at ~360px the 720-unit viewBox renders its labels
          at ~5px. The three pillars below say the same thing in text. */}
      <div className="mt-6 hidden sm:block">
        <LinkDiagram />
      </div>

      <ul className="mt-6 grid gap-5 sm:grid-cols-2">
        {PILLARS.map(({ icon: Icon, title, body }) => (
          <li key={title} className="flex gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Icon className="h-4 w-4" aria-hidden="true" />
            </span>
            <div>
              <p className="text-sm font-medium text-foreground">{title}</p>
              <p className="mt-1 text-sm text-muted-foreground">{body}</p>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-6 rounded-md bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
        <p>
          <span className="font-medium text-foreground">What does not change:</span> a Company never
          holds files, and a Company role grants no access to any workspace’s data — a workspace’s
          own owner adds people to its roster, like anyone else.
        </p>
        <p className="mt-2">
          <span className="font-medium text-foreground">How linking works:</span> a Company invites
          a workspace; its owner accepts from Workspace Controls, having read what it gives up. Only
          the Company can unlink afterwards.
        </p>
      </div>

      {onCreate && (
        <div className="mt-6 text-center">
          <Button size="lg" onClick={onCreate}>
            Create your Company
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            Takes a minute: name it, link the workspaces you own, invite your admins.
          </p>
        </div>
      )}
    </section>
  );
}
